/**
 * SearchBar — free-text search input for the Repo_List_View (Req 4).
 *
 * Responsibilities (design "React SPA components"):
 *  - Capture a free-text term of 1 to 256 characters and report the *raw* term
 *    up to the parent via `onChange` (Req 4.1). The parent owns the search
 *    state and wires the term through `applyQuery`, which feeds
 *    `searchSummaries` in `../lib/query.ts`.
 *  - Cap input length at 256 characters so a term can never exceed the bound in
 *    Req 4.1; the browser enforces this with `maxLength`.
 *
 * The search *semantics* live entirely in `searchSummaries`: the term is
 * trimmed and lower-cased there, a whitespace-only term is treated as empty
 * (Req 4.3, 4.5), and matching is case-insensitive over the searchable fields
 * (Req 4.1, 4.2). This component therefore stays purely presentational — it
 * does not trim, normalize, or filter — it just captures the raw term and
 * reports it. Keeping it controlled and side-effect-free makes it trivially
 * testable and lets the parent re-derive the displayed list (and the count and
 * no-match message of Req 4.6, 4.7) on every keystroke.
 */

/** Maximum length of a search term in characters (Req 4.1). */
export const MAX_SEARCH_TERM_LENGTH = 256;

export interface SearchBarProps {
  /** The current raw search term (controlled value). */
  value: string;
  /** Called with the new raw term whenever the user edits the input. */
  onChange: (term: string) => void;
  /** Optional placeholder text shown when the input is empty. */
  placeholder?: string;
}

/**
 * Controlled free-text search input. Renders a labeled text box bound to
 * `value`; each edit reports the raw term up through `onChange`. Input is
 * capped at {@link MAX_SEARCH_TERM_LENGTH} characters (Req 4.1); all other
 * search semantics are handled downstream by `searchSummaries`.
 *
 * A trailing "Load" affordance mirrors the reference design. Search is applied
 * live on every keystroke by the parent, so the button is a visual anchor plus
 * an explicit "apply" for keyboard/mouse users who prefer confirming a term —
 * clicking it simply reports the current value up through `onChange` again
 * (a no-op in practice since the parent already has it).
 */
export default function SearchBar({
  value,
  onChange,
  placeholder = "Enter repo name, e.g. payments-api-prod",
}: SearchBarProps) {
  return (
    <div className="ds-field">
      <label htmlFor="repo-search" className="ds-label ds-filter-grid__label">
        Search repositories
      </label>
      <div className="ds-search ds-search--with-action">
        <input
          id="repo-search"
          type="search"
          role="searchbox"
          className="ds-input"
          value={value}
          maxLength={MAX_SEARCH_TERM_LENGTH}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="ds-btn ds-btn--secondary"
          onClick={() => onChange(value)}
          aria-label="Load"
        >
          <span aria-hidden="true">{"\u{1F50D}"}</span>
          <span>Load</span>
        </button>
      </div>
    </div>
  );
}
